/**
 * The local username/password path, alongside "Sign in with GitLab" on /login.
 *
 * 🔴 THE WHOLE FLOW LIVES IN THIS COMPONENT'S MEMORY, and that is a security decision rather than a
 *   convenience. The challenge returned by a correct password is a PARTIAL CREDENTIAL — combined with
 *   a phone it becomes a session. Routing to `/login/totp?challenge=…` would put it in the URL, the
 *   browser history and any referrer; storing it would leave half a login on a shared machine's disk.
 *   ⇒ It is React state and nothing else, so closing the tab ends the attempt.
 *
 * ⭐ Mobile-first: single column, 16px inputs (anything smaller makes iOS Safari zoom on focus, which
 *   reads as the page jumping), and `inputMode="numeric"` on the code field so a phone offers digits.
 */
import { useCallback, useEffect, useReducer, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  fetchEnrolmentOffer,
  localLogin,
  verifyEnrolment,
  verifyTotp,
} from '../api/endpoints';
import { toApiError } from '../api/errors';
import { useAuth } from '../auth/AuthProvider';
import {
  canSubmitCode,
  initialLocalLoginState,
  localLoginReducer,
} from '../auth/local-login-machine';
import { Notice } from '../components/ui/Notice';
import { Spinner } from '../components/ui/Skeleton';

const FIELD_CLASS =
  'h-11 w-full rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-3 text-base text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]';

const BUTTON_CLASS =
  'flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[color:var(--color-accent)] text-sm font-semibold text-[color:var(--color-on-accent)] hover:brightness-110 disabled:pointer-events-none disabled:opacity-50';

export function LocalLoginForm() {
  const { recheck } = useAuth();
  const [state, dispatch] = useReducer(localLoginReducer, initialLocalLoginState);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const fail = useCallback((err: unknown, path: string) => {
    dispatch({ type: 'failed', message: toApiError(err, path).message });
  }, []);

  /**
   * ⭐ The QR is fetched in an effect keyed on the enrolment step, not inside the submit handler.
   *   A failure here is retryable by re-entering the password, and the reducer sends it back there
   *   with the reason — there is nothing on an empty enrolment screen for a user to act on.
   */
  useEffect(() => {
    if (state.step !== 'enrol-loading') return;
    let live = true;
    fetchEnrolmentOffer(state.challenge)
      .then((offer) => {
        if (live) dispatch({ type: 'offer-loaded', offer });
      })
      .catch((err: unknown) => {
        if (live) fail(err, '/auth/local/totp/enrol');
      });
    return () => {
      live = false;
    };
  }, [state, fail]);

  const submitCredentials = async (event: React.FormEvent) => {
    event.preventDefault();
    dispatch({ type: 'submit-credentials' });
    try {
      const challenge = await localLogin(email.trim(), password);
      // ⛔ The password is dropped from state the instant it is no longer needed. It cannot be
      //   resubmitted, cannot be read out of a React DevTools tree, and cannot end up in a re-render.
      setPassword('');
      dispatch({ type: 'challenge-issued', challenge });
    } catch (err) {
      fail(err, '/auth/local/login');
    }
  };

  const submitCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (state.step !== 'totp' && state.step !== 'enrol') return;
    dispatch({ type: 'submit-code' });

    try {
      if (state.step === 'enrol') await verifyEnrolment(state.challenge, code);
      else await verifyTotp(state.challenge, code);

      setCode('');
      dispatch({ type: 'authenticated' });
      /**
       * ⭐⭐ ASK, do not assume. The session cookie is httpOnly, so this app CANNOT see it and has no
       *   way to know it was set except by calling /auth/me. Flipping a local "signed in" flag here
       *   would produce an app that believes it is authenticated and 401s on its first real request.
       */
      recheck();
    } catch (err) {
      setCode('');
      fail(err, '/auth/local/totp/verify');
    }
  };

  if (state.step === 'done') {
    return (
      <p className="flex items-center gap-2 text-sm text-[color:var(--color-ink-muted)]">
        <Spinner label="Signing in" /> Signing you in…
      </p>
    );
  }

  if (state.step === 'credentials') {
    return (
      <form className="space-y-3" onSubmit={submitCredentials} noValidate>
        {state.error ? <Notice tone="error" title={state.error} /> : null}

        <div className="space-y-1">
          <label className="block text-xs font-medium text-[color:var(--color-ink-muted)]" htmlFor="local-email">
            Email
          </label>
          <input
            id="local-email"
            className={FIELD_CLASS}
            type="email"
            autoComplete="username"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="space-y-1">
          <label className="block text-xs font-medium text-[color:var(--color-ink-muted)]" htmlFor="local-password">
            Password
          </label>
          <input
            id="local-password"
            className={FIELD_CLASS}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <button className={BUTTON_CLASS} type="submit" disabled={state.busy || !email || !password}>
          {state.busy ? <Spinner label="Checking" /> : null}
          Continue
        </button>

        {/* ⭐ Says so UP FRONT rather than surprising them after the password. */}
        <p className="text-xs text-[color:var(--color-ink-subtle)]">
          Local accounts always require a 6-digit authenticator code. There is no sign-up — an admin
          issues an invitation.
        </p>
      </form>
    );
  }

  const enrolling = state.step === 'enrol';

  return (
    <form className="space-y-3" onSubmit={submitCode} noValidate>
      {state.error ? <Notice tone="error" title={state.error} /> : null}

      {state.step === 'enrol-loading' ? (
        <p className="flex items-center gap-2 text-sm text-[color:var(--color-ink-muted)]">
          <Spinner label="Preparing" /> Preparing your authenticator setup…
        </p>
      ) : null}

      {enrolling ? <EnrolmentPanel uri={state.offer.otpauth_uri} secret={state.offer.secret} /> : null}

      {state.step === 'enrol-loading' ? null : (
        <>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-[color:var(--color-ink-muted)]" htmlFor="totp-code">
              {enrolling ? 'Enter the code your app now shows' : '6-digit code'}
            </label>
            <input
              id="totp-code"
              className={`${FIELD_CLASS} text-center font-mono text-xl tracking-[0.4em]`}
              /**
               * ⭐ `inputMode="numeric"` gives a phone the digit pad; `type="text"` keeps it, because
               *   `type="number"` renders spinners, strips leading zeros and silently mangles `012345`
               *   — which is a perfectly valid TOTP code roughly one time in ten.
               */
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </div>

          <button
            className={BUTTON_CLASS}
            type="submit"
            disabled={('busy' in state && state.busy) || !canSubmitCode(code)}
          >
            {'busy' in state && state.busy ? <Spinner label="Verifying" /> : null}
            {enrolling ? 'Confirm and sign in' : 'Verify'}
          </button>
        </>
      )}

      <button
        type="button"
        className="w-full text-xs text-[color:var(--color-ink-subtle)] underline"
        onClick={() => {
          setCode('');
          dispatch({ type: 'restart' });
        }}
      >
        Start again
      </button>
    </form>
  );
}

/**
 * ⭐ THE SECRET IS SHOWN AS TEXT AS WELL AS A QR, and that is not a leak — it is the user's own
 *   secret, they have already proved the password, and a QR-only enrolment is impossible to complete
 *   when the browser IS the phone. On a 390px screen that is the common case, not the edge case.
 */
function EnrolmentPanel({ uri, secret }: { uri: string; secret: string }) {
  return (
    <div className="space-y-3 rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-3">
      <p className="text-sm font-semibold">Set up two-factor authentication</p>
      <p className="text-xs text-[color:var(--color-ink-muted)]">
        Scan this with an authenticator app, then enter the code it shows. This is required — a local
        account cannot sign in without it.
      </p>

      <div className="flex justify-center rounded-md bg-white p-3">
        {/* ⭐ Rendered locally as an SVG. ⛔ Never a remote chart API — that would post the secret to
            a third party, which is the exact value we just went to the trouble of encrypting. */}
        <QRCodeSVG value={uri} size={168} marginSize={1} />
      </div>

      <div className="space-y-1">
        <p className="text-xs text-[color:var(--color-ink-muted)]">Or type this key in manually:</p>
        <code className="block break-all rounded bg-[color:var(--color-surface-raised,transparent)] px-2 py-1.5 font-mono text-xs tracking-wider">
          {secret}
        </code>
      </div>
    </div>
  );
}
